import { useState } from 'react';
import { PolicyTemplate, Condition, NestedCondition, PolicyMode, ParameterType, PolicyParameter } from '../../../types/policy';
import { TabType } from '../../../types/policyEditor';
import { initialPolicyTemplate } from '../../../constants/policy';
import { policySchema } from '../../../types/policy/schema';
import { Parameters } from '../../../types/policyEditor';

/**
 * Type for the IF clause in policy rules
 * Can be either a nested condition or a negated nested condition
 */
type PolicyRuleIf = NestedCondition | { not: NestedCondition };

// Add deepMerge helper to top of file
declare function deepMerge<T>(target: any, source: any): T;

// ... after imports, before hook definition
const deepMergeImplementation = <T>(target: any, source: any): T => {
  if (typeof target !== 'object' || target === null) return source;
  const output: any = Array.isArray(target) ? [] : {};
  for (const key in target) {
    if (Object.prototype.hasOwnProperty.call(target, key)) {
      output[key] = target[key];
    }
  }
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      if (
        typeof source[key] === 'object' && source[key] !== null &&
        typeof output[key] === 'object' && output[key] !== null
      ) {
        output[key] = deepMergeImplementation(output[key], source[key]);
      } else {
        output[key] = source[key];
      }
    }
  }
  return output;
};

/**
 * Custom hook for managing policy editor state and operations
 * 
 * This hook provides comprehensive functionality for managing the entire policy state,
 * including:
 * - Basic policy information
 * - Conditions and logical operators
 * - Effect settings
 * - Parameters
 * - Tab navigation
 * 
 * @returns Object containing policy state and handlers
 */
export const usePolicyEditorState = () => {
  // Initialize policy state with template
  const [policy, setPolicy] = useState<PolicyTemplate>(initialPolicyTemplate);
  const [selectedEffect, setSelectedEffect] = useState<string>('audit');
  const [activeTab, setActiveTab] = useState<TabType>('basicInfo');

  /**
   * Updates the policy display name
   * @param value - New display name
   */
  const handleDisplayNameChange = (value: string) => {
    setPolicy(prev => ({
      ...prev,
      properties: {
        ...prev.properties,
        displayName: value
      }
    }));
  };

  /**
   * Updates the policy description
   * @param value - New description
   */
  const handleDescriptionChange = (value: string) => {
    setPolicy(prev => ({
      ...prev,
      properties: {
        ...prev.properties,
        description: value
      }
    }));
  };

  /**
   * Updates the policy category
   * @param value - New category
   */
  const handleCategoryChange = (value: string) => {
    setPolicy(prev => ({
      ...prev,
      properties: {
        ...prev.properties,
        metadata: {
          ...prev.properties.metadata,
          category: value
        }
      }
    }));
  };

  /**
   * Updates the policy mode
   * @param value - New mode
   */
  const handleModeChange = (value: string) => {
    setPolicy(prev => ({
      ...prev,
      properties: {
        ...prev.properties,
        mode: value as PolicyMode
      }
    }));
  };

  /**
   * Updates the policy effect and related settings
   * Handles special configuration for 'modify', 'auditIfNotExists', and 'deployIfNotExists' effects
   * @param value - New effect value
   */
  const handleEffectChange = (value: string) => {
    // Capitalize first letter for the default value
    const defaultValue = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    
    setSelectedEffect(value);
    setPolicy(prev => ({
      ...prev,
      properties: {
        ...prev.properties,
        parameters: {
          ...prev.properties.parameters,
          effect: {
            ...prev.properties.parameters.effect,
            allowedValues: value.toLowerCase() === 'modify' ? ['Modify', 'Disabled'] : 
                          value.toLowerCase() === 'auditifnotexists' ? ['AuditIfNotExists', 'Disabled'] :
                          value.toLowerCase() === 'deployifnotexists' ? ['DeployIfNotExists', 'Disabled'] :
                          ['Audit', 'Deny', 'Disabled'],
            defaultValue: value.toLowerCase() === 'auditifnotexists' ? 'AuditIfNotExists' :
                         value.toLowerCase() === 'deployifnotexists' ? 'DeployIfNotExists' :
                         defaultValue
          }
        },
        policyRule: {
          ...prev.properties.policyRule,
          then: value.toLowerCase() === 'modify' ? {
            effect: "[parameters('effect')]",
            details: {
              roleDefinitionIds: [],
              operations: [{
                operation: 'add',
                field: '',
                value: ''
              }]
            }
          } : value.toLowerCase() === 'auditifnotexists' ? {
            effect: "[parameters('effect')]",
            details: {
              type: '',
              existenceCondition: {}
            }
          } : value.toLowerCase() === 'deployifnotexists' ? {
            effect: "[parameters('effect')]",
            details: {
              type: '',
              roleDefinitionIds: [],
              existenceCondition: {},
              deployment: {
                properties: {
                  mode: 'Incremental',
                  template: {
                    "$schema": "https://schema.management.azure.com/schemas/2015-01-01/deploymentTemplate.json#",
                    "contentVersion": "1.0.0.0",
                    "parameters": {
                      "name": {
                        "type": "string"
                      }
                    },
                    "resources": []
                  },
                  parameters: {
                    "name": {
                      "value": "[field('fullName')]"
                    }
                  }
                }
              }
            }
          } : {
            effect: "[parameters('effect')]"
          }
        }
      }
    }));
  };

  /**
   * Handles direct JSON edits to the policy
   * Validates JSON format before updating
   * @param value - New policy JSON string
   */
  const handleEditorChange = (value: string | undefined) => {
    if (!value) return;
    try {
      const updatedPolicy = JSON.parse(value);
      setPolicy(updatedPolicy);
    } catch (error) {
      console.warn('Invalid JSON:', error);
    }
  };

  /**
   * Updates policy conditions while preserving structure
   * @param conditions - New conditions array
   */
  const handleUpdateConditions = (conditions: Condition[]) => {
    const type = getCurrentGroupType();
    const isNot = isRootNot();
    
    // Create new condition while preserving NOT wrapper if it exists
    const newIfCondition: PolicyRuleIf = isNot
      ? { not: { [type]: conditions } }
      : { [type]: conditions };

    setPolicy(prev => ({
      ...prev,
      properties: {
        ...prev.properties,
        policyRule: {
          ...prev.properties.policyRule,
          if: newIfCondition
        }
      }
    }));
  };

  /**
   * Checks if the root condition is negated
   */
  const isRootNot = (): boolean => {
    return 'not' in policy.properties.policyRule.if;
  };

  /**
   * Gets the current group type (allOf/anyOf)
   */
  const getCurrentGroupType = (): 'allOf' | 'anyOf' => {
    const ifCondition = isRootNot() 
      ? (policy.properties.policyRule.if as { not: NestedCondition }).not 
      : policy.properties.policyRule.if as NestedCondition;
    
    return 'allOf' in ifCondition ? 'allOf' : 'anyOf';
  };

  /**
   * Toggles between allOf and anyOf for the root condition group
   */
  const handleToggleGroupType = () => {
    const currentType = getCurrentGroupType();
    const newType = currentType === 'allOf' ? 'anyOf' : 'allOf';
    const conditions = getCurrentConditions();
    
    const newIfCondition: PolicyRuleIf = isRootNot()
      ? { not: { [newType]: conditions } }
      : { [newType]: conditions };
    
    setPolicy(prev => ({
      ...prev,
      properties: {
        ...prev.properties,
        policyRule: {
          ...prev.properties.policyRule,
          if: newIfCondition
        }
      }
    }));
  };

  /**
   * Toggles the NOT operator for the root condition
   */
  const handleToggleNot = () => {
    const type = getCurrentGroupType();
    const conditions = getCurrentConditions();
    
    const newIfCondition: PolicyRuleIf = isRootNot()
      ? { [type]: conditions }
      : { not: { [type]: conditions } };
    
    setPolicy(prev => ({
      ...prev,
      properties: {
        ...prev.properties,
        policyRule: {
          ...prev.properties.policyRule,
          if: newIfCondition
        }
      }
    }));
  };

  /**
   * Gets the current conditions array
   */
  const getCurrentConditions = (): Condition[] => {
    const type = getCurrentGroupType();
    const ifCondition = isRootNot() 
      ? (policy.properties.policyRule.if as { not: NestedCondition }).not 
      : policy.properties.policyRule.if as NestedCondition;
    return (ifCondition[type] || []) as Condition[];
  };

  /**
   * Updates the details section of the policy
   * Used primarily with 'modify' effect
   */
  const handleUpdateDetails = (details: any) => {
    setPolicy(prev => ({
      ...prev,
      properties: {
        ...prev.properties,
        policyRule: {
          ...prev.properties.policyRule,
          then: {
            effect: "[parameters('effect')]",
            details
          }
        }
      }
    }));
  };

  /**
   * Updates a specific parameter in the policy
   */
  const handleUpdateParameter = (name: string, parameter: any) => {
    setPolicy(prev => ({
      ...prev,
      properties: {
        ...prev.properties,
        parameters: {
          ...prev.properties.parameters,
          [name]: parameter
        }
      }
    }));
  };

  /**
   * Removes a parameter from the policy
   * Prevents removal of the 'effect' parameter
   */
  const handleRemoveParameter = (name: string) => {
    if (name === 'effect') return;
    
    setPolicy(prev => {
      const newParameters = { ...prev.properties.parameters };
      delete newParameters[name];
      return {
        ...prev,
        properties: {
          ...prev.properties,
          parameters: newParameters
        }
      };
    });
  };

  /**
   * Imports a policy from raw JSON or object, deep-merges with template, and validates.
   */
  const importPolicy = (raw: unknown): { success: true } | { success: false; errors: string[] } => {
    let parsed: unknown = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        return { success: false, errors: ['Invalid JSON format'] };
      }
    }
    // Normalize uploaded JSON: wrap raw objects lacking 'properties' if they include policyRule or mode
    let rawObj: Record<string, any>;
    if (
      typeof parsed === 'object' && parsed !== null &&
      !('properties' in parsed) &&
      ('policyRule' in parsed || 'mode' in parsed || 'parameters' in parsed)
    ) {
      rawObj = { properties: parsed as Record<string, any> };
    } else if (typeof parsed === 'object' && parsed !== null && 'properties' in parsed) {
      rawObj = parsed as Record<string, any>;
    } else {
      return { success: false, errors: ['Uploaded JSON is not a valid Azure Policy'] };
    }
    // Ensure policyRule exists under properties
    if (typeof rawObj.properties !== 'object' || !('policyRule' in rawObj.properties)) {
      return { success: false, errors: ['Uploaded JSON is not a valid Azure Policy'] };
    }
    // Deep-merge uploaded data onto our template to fill any missing fields
    const merged = deepMergeImplementation(initialPolicyTemplate, rawObj) as PolicyTemplate;

    // Always unify effect into parameter-based reference and set default value
    const rawThen = merged.properties.policyRule.then;
    let literalEffect = '';
    if (/^\[parameters\('effect'\)\]$/i.test(rawThen.effect)) {
      literalEffect = merged.properties.parameters.effect.defaultValue as string;
    } else if (typeof rawThen.effect === 'string') {
      literalEffect = rawThen.effect.trim();
    }
    
    // Normalize effect casing for comparison
    const normalizedEffect = literalEffect.toLowerCase();
    
    const defaultValueCapitalized = literalEffect.charAt(0).toUpperCase() + literalEffect.slice(1).toLowerCase();
    // Determine allowed values for effect
    const allowedValuesList = normalizedEffect === 'modify'
      ? ['Modify', 'Disabled']
      : normalizedEffect === 'auditifnotexists'
      ? ['AuditIfNotExists', 'Disabled']
      : normalizedEffect === 'deployifnotexists'
      ? ['DeployIfNotExists', 'Disabled']
      : ['Audit', 'Deny', 'Disabled'];
    
    // For AINE and DINE policies, ensure the default value is properly capitalized
    let finalDefaultValue = defaultValueCapitalized;
    if (normalizedEffect === 'auditifnotexists') {
      finalDefaultValue = 'AuditIfNotExists';
    } else if (normalizedEffect === 'deployifnotexists') {
      finalDefaultValue = 'DeployIfNotExists';
    }
    
    // Build normalized parameters map
    const normalizedParameters = Object.entries(merged.properties.parameters).reduce((acc, [key, param]) => {
      const baseParam = param as Record<string, any>;
      const meta = baseParam.metadata || {};
      // For effect, apply our override
      if (key === 'effect') {
        acc[key] = {
          ...baseParam,
          allowedValues: allowedValuesList,
          defaultValue: finalDefaultValue
        };
      } else {
        acc[key] = {
          ...baseParam,
          type: (typeof baseParam.type === 'string' ? baseParam.type : initialPolicyTemplate.properties.parameters.effect.type) as ParameterType,
          metadata: { displayName: meta.displayName || '', description: meta.description || '' },
          allowedValues: Array.isArray(baseParam.allowedValues) ? baseParam.allowedValues : [],
          defaultValue: baseParam.defaultValue !== undefined ? baseParam.defaultValue : ''
        };
      }
      return acc;
    }, {} as Record<string, any>);
    
    // Compose new policy object immutably
    const newPolicy: PolicyTemplate = {
      ...merged,
      properties: {
        ...merged.properties,
        parameters: normalizedParameters as Record<string, PolicyParameter>,
        policyRule: {
          ...merged.properties.policyRule,
          then: {
            ...rawThen,
            effect: "[parameters('effect')]"
          }
        }
      }
    };
    // Validate and set the new policy
    const result = policySchema.safeParse(newPolicy);
    if (result.success) {
      setPolicy(newPolicy);
      setSelectedEffect(normalizedEffect === 'auditifnotexists' ? 'auditIfNotExists' : 
                       normalizedEffect === 'deployifnotexists' ? 'deployIfNotExists' : 
                       normalizedEffect);
      setActiveTab('basicInfo');
      return { success: true };
    }
    const errors = result.error.errors.map(e => e.path.join('.') + ': ' + e.message);
    console.error(result.error);
    return { success: false, errors };
  };

  return {
    policy,
    setPolicy,
    selectedEffect,
    setSelectedEffect,
    activeTab,
    setActiveTab,
    handleDisplayNameChange,
    handleDescriptionChange,
    handleCategoryChange,
    handleModeChange,
    handleEffectChange,
    handleEditorChange,
    handleUpdateConditions,
    handleToggleGroupType,
    handleToggleNot,
    handleUpdateDetails,
    handleUpdateParameter,
    handleRemoveParameter,
    importPolicy
  };
}; 